/**
 * React Query hooks for feedback system
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getFeedbackList,
  getFeedbackById,
  createFeedback,
  updateFeedbackStatus,
  getFeatureSuggestions,
  getFeatureSuggestionById,
  createFeatureSuggestion,
  updateFeatureSuggestionStatus,
  getFeedbackStats,
  getFeatureSuggestionStats,
  getMyFeedback,
  getMyFeatureSuggestions,
  getAllFeedback,
  getAllFeatureSuggestions,
} from '@/services/feedback.service';
import type {
  FeedbackFormData,
  FeedbackStatus,
  FeatureSuggestionFormData,
  FeatureSuggestionStatus,
  BrowserInfo,
} from '@/types/feedback';

// =============================================================================
// QUERY KEYS
// =============================================================================

export const feedbackKeys = {
  all: ['feedback'] as const,
  lists: () => [...feedbackKeys.all, 'list'] as const,
  list: (filters?: { status?: FeedbackStatus }) => [...feedbackKeys.lists(), filters] as const,
  details: () => [...feedbackKeys.all, 'detail'] as const,
  detail: (id: string) => [...feedbackKeys.details(), id] as const,
  stats: () => [...feedbackKeys.all, 'stats'] as const,
};

export const featureSuggestionKeys = {
  all: ['feature-suggestions'] as const,
  lists: () => [...featureSuggestionKeys.all, 'list'] as const,
  list: (filters?: { status?: FeatureSuggestionStatus; category?: string }) =>
    [...featureSuggestionKeys.lists(), filters] as const,
  details: () => [...featureSuggestionKeys.all, 'detail'] as const,
  detail: (id: string) => [...featureSuggestionKeys.details(), id] as const,
  stats: () => [...featureSuggestionKeys.all, 'stats'] as const,
};

// =============================================================================
// FEEDBACK HOOKS
// =============================================================================

export function useFeedbackList(options?: { status?: FeedbackStatus; limit?: number }) {
  return useQuery({
    queryKey: feedbackKeys.list(options),
    queryFn: () => getFeedbackList(options),
  });
}

export function useFeedback(id: string) {
  return useQuery({
    queryKey: feedbackKeys.detail(id),
    queryFn: () => getFeedbackById(id),
    enabled: !!id,
  });
}

export function useFeedbackStats() {
  return useQuery({
    queryKey: feedbackKeys.stats(),
    queryFn: getFeedbackStats,
  });
}

export function useCreateFeedback() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      formData,
      user,
      pageInfo,
      browserInfo,
    }: {
      formData: FeedbackFormData;
      user: { id: string; name?: string; email?: string };
      pageInfo: { url: string; title?: string };
      browserInfo: BrowserInfo;
    }) => createFeedback(formData, user, pageInfo, browserInfo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feedbackKeys.all });
    },
  });
}

export function useUpdateFeedbackStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      status,
      adminNotes,
      resolvedBy,
    }: {
      id: string;
      status: FeedbackStatus;
      adminNotes?: string;
      resolvedBy?: string;
    }) => updateFeedbackStatus(id, status, adminNotes, resolvedBy),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: feedbackKeys.all });
      queryClient.invalidateQueries({ queryKey: feedbackKeys.detail(variables.id) });
    },
  });
}

// =============================================================================
// FEATURE SUGGESTION HOOKS
// =============================================================================

export function useFeatureSuggestions(options?: {
  status?: FeatureSuggestionStatus;
  category?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: featureSuggestionKeys.list(options),
    queryFn: () => getFeatureSuggestions(options),
  });
}

export function useFeatureSuggestion(id: string) {
  return useQuery({
    queryKey: featureSuggestionKeys.detail(id),
    queryFn: () => getFeatureSuggestionById(id),
    enabled: !!id,
  });
}

export function useFeatureSuggestionStats() {
  return useQuery({
    queryKey: featureSuggestionKeys.stats(),
    queryFn: getFeatureSuggestionStats,
  });
}

export function useCreateFeatureSuggestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      formData,
      user,
    }: {
      formData: FeatureSuggestionFormData;
      user: { id: string; name?: string; email?: string };
    }) => createFeatureSuggestion(formData, user),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featureSuggestionKeys.all });
    },
  });
}

export function useMyFeedback(userEmail: string | undefined) {
  return useQuery({
    queryKey: [...feedbackKeys.all, 'my', userEmail] as const,
    queryFn: () => getMyFeedback(userEmail!),
    enabled: !!userEmail,
  });
}

export function useMyFeatureSuggestions(userEmail: string | undefined) {
  return useQuery({
    queryKey: [...featureSuggestionKeys.all, 'my', userEmail] as const,
    queryFn: () => getMyFeatureSuggestions(userEmail!),
    enabled: !!userEmail,
  });
}

export function useAllFeedback(enabled = true) {
  return useQuery({
    queryKey: [...feedbackKeys.all, 'all'] as const,
    queryFn: getAllFeedback,
    enabled,
  });
}

export function useAllFeatureSuggestions(enabled = true) {
  return useQuery({
    queryKey: [...featureSuggestionKeys.all, 'all'] as const,
    queryFn: getAllFeatureSuggestions,
    enabled,
  });
}

export function useUpdateFeatureSuggestionStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      status,
      adminNotes,
    }: {
      id: string;
      status: FeatureSuggestionStatus;
      adminNotes?: string;
    }) => updateFeatureSuggestionStatus(id, status, adminNotes),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: featureSuggestionKeys.all });
      queryClient.invalidateQueries({ queryKey: featureSuggestionKeys.detail(variables.id) });
    },
  });
}
